/**
 * Run a solve across worker islands until a wall-clock deadline, migrating the
 * best build between them.
 *
 * Island count tracks `hardwareConcurrency` rather than a fixed number. The
 * production 4 came from Cloud Run's vCPU count, and measurement (island_sweep,
 * total evals held constant) showed more islands strictly better on hard requests
 * — +6.6% from 1 to 16 — and neutral on easy ones, with no case where it hurt.
 *
 * The budget is time, not evaluations. An eval count has to assume a throughput,
 * and the previous constant (1M evals per requested second) assumed one thread's
 * worth while sixteen workers deliver roughly 3.1M/s — so a 7s request finished
 * in about 2.2s with the progress bar stuck near 60%. Throughput varies with
 * core count, browser and pool size, so the fix is to stop guessing: search in
 * chunks and keep going until the clock says stop.
 */
import { fetchKernelAssets } from './kernel';
import { TOURNAMENT_SHARES, tournamentWidths } from './tournament';
import {
    newStats, mergeStats, solveMetrics,
    newProgressTrack, recordImprovement, recordSeed, settleDelivered,
} from './report';

/**
 * Never spawn more than this many workers, however many cores are reported.
 *
 * Measured in Chrome on a 20-core machine, 7s budget, same request:
 *
 *   islands  prepare  evals   aggregate/s  per-island/s  wall
 *   1        -        -       196k         196k          -
 *   4        1291ms   2.12M   294k         73.5k         8.5s
 *   8        1746ms   3.82M   534k         66.8k         8.9s
 *   16       2747ms   3.84M   523k         32.7k         10.1s
 *
 * Throughput saturates around 530k/s: the islands are not core-bound (most of
 * the 20 sit idle) but memory-bound, each streaming its own copy of the packed
 * dataset. Past 8 the extra islands buy no evaluations at all and cost a second
 * of redundant precompute, so 16 was strictly worse than 8 on every axis.
 *
 * The native sweep that chose 16 held total evals constant and shared one blob
 * across threads — neither is true in a browser, where every worker re-prepares
 * and re-loads the data.
 */
const MAX_ISLANDS = 8;

/** Fallback when the browser does not report core count. */
const DEFAULT_ISLANDS = 4;

/** Evals in the first chunk, before any measurement exists to size from. */
const CALIBRATION_EVALS = 60_000;

/**
 * How long a chunk should take. Short enough that islands trade elites often
 * and the deadline is not overshot by much; long enough that postMessage and
 * scheduling stay noise next to the search itself.
 */
const TARGET_CHUNK_MS = 400;

/** Stop rather than start a chunk that cannot meaningfully finish. */
const MIN_REMAINING_MS = 120;

/** Chunk bounds, so one slow or fast measurement cannot produce a silly budget. */
const MIN_CHUNK_EVALS = 20_000;
const MAX_CHUNK_EVALS = 4_000_000;

/** Rows in the report's top-builds table, after pooling every island's list. */
const ELITE_POOL_ROWS = 8;

/** Candidates an island estimates per message, so faster islands take more. */
const ESTIMATE_SLICE = 48;

/** How many islands to run for this device. */
export function islandCount() {
    const cores = Number(navigator?.hardwareConcurrency) || DEFAULT_ISLANDS;
    return Math.max(1, Math.min(MAX_ISLANDS, cores));
}

/**
 * Lexicographic build comparison, matching the kernel's `Fit` ordering:
 * hard targets first, then soft score, then the untargeted tie-break.
 */
function isBetter(a, b) {
    if (!a) return false;
    if (!b) return true;
    if (a.hard !== b.hard) return a.hard > b.hard;
    if (a.soft !== b.soft) return a.soft > b.soft;
    return (a.untargeted || 0) > (b.untargeted || 0);
}

/** One request/response exchange with a worker. Never rejects. */
function ask(worker, message, expect, timeoutMs = 0) {
    return new Promise((resolve) => {
        let timer = null;
        const onMessage = (e) => {
            const data = e.data || {};
            if (data.type !== expect) return;
            cleanup();
            resolve(data);
        };
        const onError = (err) => {
            cleanup();
            resolve({ type: expect, ok: false, error: (err && err.message) || 'island crashed' });
        };
        const cleanup = () => {
            if (timer) clearTimeout(timer);
            worker.removeEventListener('message', onMessage);
            worker.removeEventListener('error', onError);
        };
        worker.addEventListener('message', onMessage);
        worker.addEventListener('error', onError);
        // Only the handshake sets one. A search chunk is answered when it is
        // answered, and a deadline there would abandon work that is still
        // running; a worker that never loads answers nothing, ever.
        if (timeoutMs > 0) {
            timer = setTimeout(() => {
                cleanup();
                resolve({ type: expect, ok: false, error: 'island did not answer' });
            }, timeoutMs);
        }
        worker.postMessage(message);
    });
}

/**
 * The islands exist but their script never loaded.
 *
 * Its own reason because it means something specific and actionable: the page
 * is asking for a hashed chunk that a deploy has since replaced. Nothing the
 * app can fetch will fix that — only a reload, with the URLs of the version
 * that is actually published.
 */
export const WORKER_SCRIPT_UNAVAILABLE = 'worker script unavailable';

/**
 * Generous: this covers fetching and starting the worker script on a cold
 * cache and a slow connection. It is a backstop, not the mechanism — a script
 * that 404s fires an error event immediately, and that is what usually answers.
 */
const PING_TIMEOUT_MS = 10_000;

/**
 * The one place an island is spawned.
 *
 * Shared rather than repeated: webpack treats every literal
 * `new Worker(new URL(...))` as its own worker entry, so a second copy of this
 * expression emits a second ~60KB copy of the whole worker bundle under a
 * different hashed name. One site also means the probe below checks the exact
 * URL the pool will use, not a twin of it.
 */
function spawnIsland() {
    return new Worker(new URL('./solve.worker.js', import.meta.url));
}

/**
 * Whether this page can still load its solver.
 *
 * Asked by trying, because nothing cheaper is accurate. The obvious proxy — has
 * a deploy happened since this page loaded — answers a much broader question
 * than the one that matters: chunk filenames are content hashes, so a deploy
 * that leaves the worker alone leaves its URL valid and the page solving
 * perfectly. Measured on this codebase, a one-line edit elsewhere reissued the
 * entry bundle and left all 25 other chunks, this worker included, untouched.
 * Prompting on that would be 20 interruptions a day for nothing broken.
 *
 * A browser that cannot spawn workers at all reports reachable: it has nothing
 * to gain from reloading, and local solving was never on the table for it.
 */
export async function islandsReachable() {
    let worker = null;
    try {
        worker = spawnIsland();
    } catch (_err) {
        return true;
    }
    try {
        const pong = await ask(worker, { type: 'ping', id: 0 }, 'pong', PING_TIMEOUT_MS);
        return !!pong.ok;
    } finally {
        worker.terminate();
    }
}

/**
 * Fetch the assets, spawn the islands, and prove both are usable.
 *
 * Separate from searching so the caller can find out whether solving locally is
 * possible *before* it spends anything on the attempt — a generation from the
 * user's daily cap, in particular. Everything here is free to abandon.
 *
 * The ping is the point. `new Worker(url)` for a script that no longer exists
 * does not throw; it resolves into a worker that quietly never answers, so
 * without a handshake the first sign of trouble is eight islands failing to
 * prepare, several seconds later and already paid for.
 *
 * @returns {Promise<{assets: object, workers: Worker[], live: number[],
 *   failures: string[], spawnedAt: number, dispose: Function}>}
 */
export async function preparePool({ digest, build, islands = islandCount() } = {}) {
    const spawnedAt = performance.now();
    const failures = [];

    // Fetched and compiled once for every island. A WebAssembly.Module is
    // structured-cloneable and V8 reuses its compiled code, so this replaces
    // N fetches and N compilations of the same 790KB with one of each.
    const assets = await fetchKernelAssets(digest, build);

    const workers = [];
    for (let i = 0; i < Math.max(1, islands); i += 1) {
        try {
            workers.push(spawnIsland());
        } catch (err) {
            // A browser that cannot spawn workers still gets a solve from
            // whichever islands did start.
            failures.push(`worker spawn failed: ${err.message}`);
        }
    }
    const dispose = () => workers.forEach((w) => w.terminate());
    if (workers.length === 0) throw new Error(failures[0] || 'no solver islands started');

    const pongs = await Promise.all(
        workers.map((w, i) => ask(w, { type: 'ping', id: i }, 'pong', PING_TIMEOUT_MS)),
    );
    const live = [];
    pongs.forEach((p, i) => {
        if (p.ok) live.push(i);
        else failures.push(p.error || 'island did not answer');
    });
    if (live.length === 0) {
        dispose();
        throw new Error(WORKER_SCRIPT_UNAVAILABLE);
    }

    return { assets, workers, live, failures, spawnedAt, dispose };
}

/**
 * Estimate every tournament candidate, slices handed to whichever island is
 * free, and return them best-first.
 */
async function estimateArms({ workers, live, armCount, failures }) {
    const rows = [];
    let next = 0;
    await Promise.all(live.map(async (i) => {
        while (next < armCount) {
            const from = next;
            next = Math.min(armCount, next + ESTIMATE_SLICE);
            // eslint-disable-next-line no-await-in-loop
            const r = await ask(workers[i], { type: 'estimate', id: i, from, to: next }, 'estimates');
            if (!r.ok) {
                failures.push(r.error || 'estimate failed');
                return;
            }
            rows.push(...r.rows);
        }
    }));
    // Ranked on score, not hard targets first as finished builds are. A
    // greedy pass that missed MP 6 only spent its few tries per slot on
    // damage, which the searches then fix: ranked hard-first, the best
    // candidate measured (844 a turn) fell below every weapon whose quick
    // build happened to reach MP. A build that breaks a rule (hard < 0) is no
    // candidate at all.
    return rows
        .filter((row) => row.hard >= 0)
        .sort((a, b) => (b.soft - a.soft) || (a.arm - b.arm));
}

/** Best-first order on `isBetter`. */
function compareBuilds(a, b) {
    if (isBetter(a, b)) return -1;
    if (isBetter(b, a)) return 1;
    return 0;
}

/**
 * The tournament's rounds over `ranked` candidates, `searchMs` of search left.
 *
 * Each `plan` call is one chunk per island: islands take the surviving
 * candidates in turn, so every candidate of a round gets the same share of it,
 * and several islands share one when few are left. A round ends when its share
 * of the time is spent (`due`), and the next keeps the candidates whose builds
 * score best *finalized* (`advance`) — not on the search score, which ranks
 * gear with no characteristic points invested and disagrees: narrowing on it
 * sent 36 of 72 low-spec runs below 95% of the best build, on the finalized
 * score 19.
 */
function newTournament(ranked, searchMs, islands) {
    const budgets = TOURNAMENT_SHARES.map((share) => share * searchMs);
    const widths = tournamentWidths(islands, searchMs);
    let round = 0;
    let roundStart = null;
    let cursor = 0;
    let alive = ranked.slice(0, widths[0]).map((row) => ({ arm: row.arm, best: row }));
    return {
        alive: () => alive,
        due(nowMs) {
            if (roundStart === null) roundStart = nowMs;
            return round < widths.length - 1 && nowMs - roundStart >= budgets[round];
        },
        /** Next round, keeping the best by `finals` (one finalized result per survivor). */
        advance(nowMs, finals) {
            round += 1;
            roundStart = nowMs;
            alive = alive
                .map((c, k) => ({ c, f: finals[k] }))
                .sort((a, b) => compareBuilds(a.f || a.c.best, b.f || b.c.best))
                .map(({ c }) => c)
                .slice(0, widths[round]);
            cursor = 0;
        },
        plan(live, nowMs) {
            if (roundStart === null) roundStart = nowMs;
            const jobs = live.map((i, k) => {
                const candidate = alive[(cursor + k) % alive.length];
                return { i, arm: candidate.arm, start: candidate.best.items };
            });
            cursor = (cursor + live.length) % alive.length;
            return jobs;
        },
        record(arm, result) {
            const candidate = alive.find((c) => c.arm === arm);
            if (candidate && isBetter(result, candidate.best)) candidate.best = result;
        },
    };
}

/**
 * Each candidate's best build finalized — characteristic points allocated — in
 * candidate order, null where a worker failed. Spread over the islands, one
 * request at a time per island: replies carry no id to match them by.
 */
async function finalizeCandidates(candidates, workers, live, evals) {
    const out = new Array(candidates.length).fill(null);
    await Promise.all(live.map(async (i, k) => {
        for (let c = k; c < candidates.length; c += live.length) {
            // eslint-disable-next-line no-await-in-loop
            const r = await ask(workers[i], { type: 'finalize', id: i, items: candidates[c].best.items, evals }, 'final');
            if (r.ok) out[c] = r.result;
        }
    }));
    return out;
}

/**
 * Search `request` across a prepared pool until `seconds` have elapsed.
 *
 * @param {object} opts
 * @param {object} opts.pool from `preparePool`; the caller owns its lifetime
 * @param {object} opts.request shape from `toKernelRequest`
 * @param {number} [opts.seconds] wall-clock budget
 * @param {(p: {elapsedMs: number, totalMs: number, evals: number, best: object|null}) => void} [opts.onProgress]
 * @returns {Promise<{result: object, islands: number, ms: number, evals: number, rounds: number, failures: string[]}>}
 */
export async function searchInPool({
    pool,
    request,
    seconds = 4,
    signal,
    onProgress,
    onPrepared,
    onFinalize,
} = {}) {
    const { workers, assets, dispose } = pool;
    const failures = pool.failures.slice();
    const totalMs = Math.max(0, seconds * 1000);
    // The pool's own clock, so preparing still reads as everything the user
    // waited through before the search: fetching, compiling, spawning, and the
    // precompute below.
    const spawned = pool.spawnedAt;
    // Set once preparing is done. The budget is search time: preparing 16
    // islands takes seconds of wall clock because they all compete for cores,
    // and charging that to the user's budget would silently halve the search.
    let started = spawned;

    // Cancelling has to reach the islands. Without this the pool kept searching
    // to its deadline after the user pressed stop — the result was discarded,
    // but eight workers ran on, and generating again stacked eight more on top.
    // terminate() is the only way to interrupt a worker mid-chunk: the kernel
    // is inside a synchronous wasm call and will not check a flag.
    const onAbort = () => dispose();
    if (signal) signal.addEventListener('abort', onAbort, { once: true });

    try {
        // Distinct seeds are what make the islands explore differently; the
        // kernel ORs the seed with 1, so zero is never handed out.
        const seedFor = (i, round) => ((i + 1) * 0x9e3779b1 + round * 0x85ebca6b) >>> 0;

        const { module, dataset } = assets;

        // Only the islands that answered the handshake; the rest are dead
        // weight the pool already accounted for.
        const answered = pool.live;
        // One prepare seed for a tournament. Each candidate's estimate starts
        // partly from the island's seed build, so islands with different seeds
        // rate their slices on different footings and the ranking mixes them:
        // a level 200 winner ranked 74th in the browser against ~9th natively.
        // Its searches still differ — every chunk carries its own seed.
        const tournamentRequest = !!request?.best_weapon;
        const ready = await Promise.all(
            answered.map((i) => ask(workers[i], {
                type: 'prepare',
                id: i,
                module,
                dataset,
                request,
                seed: seedFor(tournamentRequest ? 0 : i, 0),
            }, 'ready')),
        );
        const live = [];
        ready.forEach((r, k) => {
            if (r.ok) live.push(answered[k]);
            else failures.push(r.error || 'prepare failed');
        });
        if (live.length === 0) throw new Error(failures[0] || 'all solver islands failed to prepare');
        // How many tournament candidates to estimate, best potential first —
        // all of them without a potential table. Every island prepared the
        // same request, so they agree on it.
        const armCount = Number(ready.find((r) => r.ok)?.arms) || 0;

        // What the search is about to choose from. Asked of one island because
        // they all prepared the same pools, and asked before the clock starts
        // because it is report data, not search.
        const prepared = await ask(workers[live[0]], { type: 'report', id: live[0] }, 'report');
        const report = (prepared.ok && prepared.report) || null;

        // The seed build with points allocated: where the convergence chart
        // starts, and the baseline every later build must beat. A zero-eval
        // search is how an island hands back the seed it prepared. Before the
        // clock, like the report, and never fatal — without it the chart just
        // starts at the first round.
        const seedSearch = await ask(workers[live[0]], {
            type: 'search', id: live[0], evals: 0, seed: seedFor(live[0], 0), start: null,
        }, 'chunk');
        const seedFinal = seedSearch.ok
            ? await ask(workers[live[0]], {
                type: 'finalize', id: live[0], items: seedSearch.result.items, evals: 0,
            }, 'final')
            : seedSearch;

        // Searching starts now: the deadline and the progress gauge both begin
        // here, so preparing shows as its own state rather than eating budget.
        started = performance.now();
        if (onPrepared) onPrepared({ prepareMs: Math.round(started - spawned), islands: live.length });

        // Report state. `track.series` is one point per *finalized* improvement,
        // not per search improvement: the two objectives disagree, and a chart
        // of the search winner would step down where the displayed score does
        // not.
        const stats = newStats();
        const track = newProgressTrack();
        if (seedFinal.ok) recordSeed(track, seedFinal.result.soft);
        const evalsByIsland = new Map(live.map((i) => [i, 0]));

        let best = null;
        // Best build after point allocation — what the user sees and receives.
        // Kept apart from `best` because the search and finalize objectives
        // disagree; see the note where it is updated.
        let bestFinal = seedFinal.ok ? seedFinal.result : null;
        let evals = 0;
        let round = 0;
        // Per-island measured throughput, so a slow core gets a smaller chunk
        // and the round is not paced by its worst member.
        const evalsPerMs = new Map(live.map((i) => [i, null]));

        const remaining = () => totalMs - (performance.now() - started);
        const elapsed = () => performance.now() - started;

        // The tournament's first round: every candidate estimated, spread over
        // the islands. Inside the budget — it is search, the cheap kind.
        let tournament = null;
        let estimateMs = 0;
        if (armCount > 0) {
            const ranked = await estimateArms({ workers, live, armCount, failures });
            estimateMs = Math.round(elapsed());
            if (ranked.length > 0) {
                tournament = newTournament(ranked, remaining(), live.length);
                best = ranked[0];
            }
        }

        while (remaining() > MIN_REMAINING_MS) {
            if (signal?.aborted) throw new Error('cancelled');
            round += 1;
            const budgetMs = Math.min(TARGET_CHUNK_MS, remaining());
            // Snapshot the mutable loop state. The per-island closures below
            // must see this round's values, not whatever the next iteration
            // reassigns — which is exactly what `no-loop-func` guards against.
            const roundNow = round;
            // Migration: every island continues from the best build any island
            // has found — independent runs measured worse. In a tournament each
            // island continues its own candidate's best instead.
            if (tournament && tournament.due(elapsed())) {
                // eslint-disable-next-line no-await-in-loop
                const finals = await finalizeCandidates(tournament.alive(), workers, live, evals);
                tournament.advance(elapsed(), finals);
            }
            const startItems = best ? best.items : null;
            const jobs = tournament
                ? tournament.plan(live, elapsed())
                : live.map((i) => ({ i, arm: null, start: startItems }));

            const results = await Promise.all(jobs.map(({ i, arm, start }) => {
                const rate = evalsPerMs.get(i);
                const want = rate === null
                    ? CALIBRATION_EVALS
                    : Math.round(rate * budgetMs);
                const chunk = Math.max(MIN_CHUNK_EVALS, Math.min(MAX_CHUNK_EVALS, want));
                return ask(workers[i], {
                    type: 'search',
                    id: i,
                    evals: chunk,
                    seed: seedFor(i, roundNow),
                    arm,
                    start,
                }, 'chunk').then((r) => ({ i, arm, chunk, r }));
            }));

            let anyOk = false;
            let improved = false;
            for (const { i, arm, chunk, r } of results) {
                if (!r.ok) {
                    failures.push(r.error || 'island failed');
                    continue;
                }
                if (tournament) tournament.record(arm, r.result);
                anyOk = true;
                const ran = Number(r.result?.evals || chunk);
                evals += ran;
                evalsByIsland.set(i, (evalsByIsland.get(i) || 0) + ran);
                mergeStats(stats, r.result?.stats);
                if (r.ms > 0) evalsPerMs.set(i, chunk / r.ms);
                if (isBetter(r.result, best)) {
                    best = r.result;
                    improved = true;
                }
            }
            if (!anyOk) break;

            // Finalize each new incumbent and keep the best *finalized* build
            // separately from the best searched one.
            //
            // The two objectives disagree: the search scores gear with no
            // characteristic points invested, finalize then allocates all 995.
            // A build that wins the search can lose after allocation — measured
            // on a real request, search 462.49 finalized to 505.39 while search
            // 462.90 finalized to only 505.14. Showing the search winner
            // therefore made the displayed score go *down* mid-run, which is
            // what users saw as 578 dropping to 577.
            //
            // `best` still drives migration, because that is the objective the
            // islands are actually optimising. `bestFinal` is what the user is
            // shown and what is returned.
            if (improved) {
                const fin = await ask(workers[live[0]], {
                    type: 'finalize', id: live[0], items: best.items, evals,
                }, 'final');
                if (fin.ok && isBetter(fin.result, bestFinal)) {
                    bestFinal = fin.result;
                    recordImprovement(
                        track, Math.round(performance.now() - started), bestFinal.soft,
                    );
                }
            }
            const elapsedMs = Math.round(performance.now() - started);
            if (onProgress) {
                onProgress({
                    elapsedMs,
                    totalMs,
                    evals,
                    best,
                    preview: bestFinal,
                    metrics: solveMetrics({
                        stats, evals, elapsedMs, totalMs, bestFinal, track,
                    }),
                    // Copied, not handed over: the caller puts this straight
                    // into React state, and a shared array would keep mutating
                    // underneath it without ever changing identity.
                    scoreTimeSeries: track.series.slice(),
                });
            }
        }

        if (!best) throw new Error(failures[0] || 'no island produced a build');

        // The tournament's winner is the finalist that scores best finalized,
        // which need not be the best search score among them.
        if (tournament) {
            const finalists = tournament.alive();
            const finals = await finalizeCandidates(finalists, workers, live, evals);
            let pick = -1;
            finals.forEach((f, k) => {
                if (f && (pick < 0 || isBetter(f, finals[pick]))) pick = k;
            });
            if (pick >= 0) {
                best = finalists[pick].best;
                if (isBetter(finals[pick], bestFinal)) bestFinal = finals[pick];
            }
        }

        // Points are allocated once, at the end: doing it per chunk would spend
        // the deadline on finalize rather than on search. It sits outside the
        // budget and outside the gauge — the caller only relabels.
        if (onFinalize) onFinalize();
        // Finalize the search winner one last time, then deliver whichever of it
        // and the running best-finalized is actually better. Without the
        // comparison the last round could hand back a build worse than one
        // already shown.
        // Finalize on every island, not just one.
        //
        // Point allocation reorders builds — the search ranks gear with nothing
        // invested — and each island holds its own elite pool from its own
        // basin. Asking only one island to finalize throws the others away.
        // Measured on the production request: +0.10 mean, +0.25 best, never
        // negative. Small, but it is free at this point in the run.
        //
        // The same is not true *within* an island: migration pins them all to
        // one incumbent, so an island's own elites are neighbours and choosing
        // among them gained exactly nothing (+0.00 over six seeds).
        const finals = await Promise.all(live.map((i) => ask(workers[i], {
            type: 'finalize', id: i, items: best.items, evals,
        }, 'final')));
        let delivered = bestFinal;
        for (const f of finals) {
            if (f.ok && isBetter(f.result, delivered)) delivered = f.result;
        }
        if (!delivered) {
            throw new Error((finals.find((f) => !f.ok) || {}).error || 'finalize failed');
        }

        const searchMs = Math.round(performance.now() - started);
        if (isBetter(delivered, bestFinal)) {
            settleDelivered(track, searchMs, delivered.soft);
        }

        // Every island finalized the same gear, but each chose among its own
        // banked elites, so the winners differ — that spread is what the
        // per-worker chart shows, and its distinct builds are the top-builds
        // table. Both come free from a round that already ran.
        const workerMetrics = live.map((i, k) => ({
            worker_index: i,
            final_score: finals[k].ok ? finals[k].result.soft : null,
            eval_count: evalsByIsland.get(i) || 0,
        })).filter((w) => w.final_score != null);

        // Top builds: every island's finalize ranked its own banked elites, so
        // pooling their lists and deduping gives real alternates rather than
        // the same gear eight times. Scored after point allocation, which is
        // what makes them comparable to the headline score.
        const seenItems = new Set();
        const elitePool = [];
        for (const f of finals) {
            if (!f.ok) continue;
            for (const alt of f.result.elites || []) {
                const key = alt.items.join(',');
                if (seenItems.has(key)) continue;
                seenItems.add(key);
                elitePool.push({
                    score: alt.soft,
                    hard_score: alt.hard,
                    item_ids: alt.items,
                });
            }
        }
        elitePool.sort((a, b) => (b.hard_score - a.hard_score) || (b.score - a.score));
        elitePool.length = Math.min(elitePool.length, ELITE_POOL_ROWS);

        return {
            report: {
                metrics: {
                    ...solveMetrics({
                        stats,
                        evals,
                        elapsedMs: searchMs,
                        totalMs,
                        bestFinal: delivered,
                        track,
                    }),
                    // Preparing is real wall time the user waited through, and
                    // naming it separately is what makes a slow start legible
                    // as precompute rather than as a slow search.
                    prepare_ms: Math.round(started - spawned),
                    islands: live.length,
                    // Find-the-best-weapon only: how many (weapon, forging)
                    // were estimated and what that cost of the search time.
                    ...(tournament ? { weapon_candidates: armCount, weapon_estimate_ms: estimateMs } : {}),
                },
                scoreTimeSeries: track.series,
                workerMetrics,
                elitePool,
                candidates: report?.candidates || null,
                setCandidates: report?.set_candidates || null,
            },
            result: delivered,
            islands: live.length,
            ms: Math.round(performance.now() - spawned),
            searchMs: Math.round(performance.now() - started),
            prepareMs: Math.round(started - spawned),
            evals,
            rounds: round,
            failures,
        };
    } finally {
        if (signal) signal.removeEventListener('abort', onAbort);
        dispose();
    }
}
